import { ReactNode } from 'react';
import { EllipsisVertical, User } from 'lucide-react';
import { useGlobalStore } from '../store/GlobalStore';
import { Avatar, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from './ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from './ui/sidebar';

/**
 * The signed-in user's row at the foot of a sidebar, opening a menu of
 * `children` (DropdownMenuItems). Shared by the app's and the admin app's
 * sidebars, which differ only in what the menu offers.
 */
export function SidebarUserMenu({ children }: { children: ReactNode }) {
  const { isMobile } = useSidebar();
  const userClass = useGlobalStore(state => state.userClass);

  const name = [userClass?.firstName, userClass?.lastName].filter(Boolean).join(' ');
  const email = userClass?.email ?? '';
  const displayName = name || email;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                className="data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground"
              >
                <Avatar aria-hidden className="size-8 after:hidden">
                  <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground">
                    <User className="size-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-sidebar-accent-foreground">{displayName}</span>
                  {/* PropelAuth does not require a name, in which case
                      displayName is already the email -- don't print it twice. */}
                  {email && email !== displayName && (
                    <span className="truncate text-xs">{email}</span>
                  )}
                </div>
                <EllipsisVertical className="ml-auto" />
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
            className="min-w-56"
          >
            {children}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
